import { Box, Button, Grid, Typography } from '@mui/material';
import { AvatarSize, Route } from 'app/enums';
import { UserEntity } from 'app/interfaces/entity';
import { Country } from 'country-state-city';
import DOMPurify from 'dompurify';
import React, { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyPlaceholder } from 'shared/components/EmptyPlaceholder';
import { UserAvatar } from 'shared/components/UserAvatar';
import { useDefaultUserMenuActions } from 'shared/hooks';
import { SocialNetworkLinksList } from '../SocialNetworkLinks';
import { useStyles } from './PlayerProfile.style';
import { ActionGuard } from 'shared/components/ActionGuard';

interface PlayerProfileProps {
  user: UserEntity | null;
  isMe: boolean;
}

export const PlayerProfile: FC<PlayerProfileProps> = ({ user, isMe }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { privateMessageHandler, challengeHandler } = useDefaultUserMenuActions();

  const country = useMemo(() => {
    return user?.country ? Country.getCountryByCode(user.country)?.name : '';
  }, [user?.country]);

  const onChallengeHandler = useCallback(() => {
    if (!user) {
      return;
    }

    challengeHandler(user);
  }, [challengeHandler, user]);

  const onPrivateMessageHandler = useCallback(() => {
    if (!user) {
      return;
    }

    privateMessageHandler(user);
  }, [privateMessageHandler, user]);

  const onEditHandler = useCallback(() => {
    if (!user || !isMe) {
      return;
    }

    navigate(Route.PlayerMyProfileEdit);
  }, [navigate, user, isMe]);

  return (
    <Box className={classes.root}>
      <Grid container>
        <Grid item sm={8} xs={12} className={classes.player}>
          <UserAvatar user={user} size={AvatarSize.Large} withOnlineIndicator={false} />
          <Box>
            <Typography variant="h5" className={classes.username}>
              {user?.userName}
            </Typography>
            {country && (
              <Typography variant="subtitle1" className={classes.country}>
                {country}
              </Typography>
            )}
            <Box className={classes.socialLinks}>
              <SocialNetworkLinksList links={user?.socialNetworkLinks ?? {}} />
            </Box>
          </Box>
        </Grid>
        <Grid item sm={4} xs={12}>
          {isMe && (
            <Box className={classes.actions}>
              <Button
                size="medium"
                color="primary"
                variant="outlined"
                onClick={onEditHandler}
                className={classes.button}
              >
                Edit
              </Button>
            </Box>
          )}
          {!isMe && (
            <Box className={classes.actions}>
              <ActionGuard>
                <Button
                  size="medium"
                  color="primary"
                  variant="outlined"
                  className={classes.button}
                  onClick={onPrivateMessageHandler}
                >
                  Private Message
                </Button>
              </ActionGuard>
              <Button
                size="medium"
                color="primary"
                variant="outlined"
                className={classes.button}
                onClick={onChallengeHandler}
              >
                Challenge
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      <Box className={classes.bioContainer}>
        <Typography variant="h5" fontWeight="bold" pb={1}>
          Biography
        </Typography>
        {user?.bio ? (
          <Box
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(user?.bio, { ADD_ATTR: ['target'] }),
            }}
          />
        ) : (
          <Box height="100%" display="flex">
            <EmptyPlaceholder text="No biography added" />
          </Box>
        )}
      </Box>
    </Box>
  );
};
